import type { DeviceType } from '@/app/util/env'
import type { ILoggedInUser } from 'types'

export const UPGRADED_ONBOARDING = 'upgraded_onboarding'
export const UPGRADED_ONBOARDING_HASH = '#upgraded-onboarding'
export const STATUS_PENDING = 0
export const STATUS_COMPLETED = 2

const AUTO_OPEN_EXCLUDED_PATHS = ['/dashboard', '/supercut', '/create', '/upload']

export type UserExperience = {
    experience_id: string
    status: number
}

export type UserExperienceCollection =
    | UserExperience[]
    | Record<string, UserExperience | unknown>
    | {
          results?: UserExperience[]
      }

type EligibilityUser = Pick<ILoggedInUser, 'is_upgraded'> | null | undefined

type FetchEligibilityParams = {
    deviceType: DeviceType
    user: EligibilityUser
    hasHash: boolean
    pathname: string
}

type AutoOpenParams = {
    deviceType: DeviceType
    user: EligibilityUser
    experience: UserExperience | null | undefined
    pathname: string
}

type HashOpenParams = {
    deviceType: DeviceType
    user: EligibilityUser
    hasHash: boolean
}

const isUserExperience = (value: unknown): value is UserExperience => {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as UserExperience).experience_id === 'string' &&
        typeof (value as UserExperience).status === 'number'
    )
}

export const hasUpgradedOnboardingHash = (hash?: string | null) => hash === UPGRADED_ONBOARDING_HASH

export const shouldMountUpgradedOnboarding = (deviceType: DeviceType) => deviceType === 'desktop'

export const isAutoOpenExcludedPath = (pathname: string) => {
    return AUTO_OPEN_EXCLUDED_PATHS.some((excludedPath) => pathname.includes(excludedPath))
}

export const isPendingExperience = (experience?: UserExperience | null) => experience?.status === STATUS_PENDING

export const isCompletedExperience = (experience?: UserExperience | null) => experience?.status === STATUS_COMPLETED

export const getExperienceValues = (experiences?: UserExperienceCollection | null): UserExperience[] => {
    if (!experiences) {
        return []
    }

    if (Array.isArray(experiences)) {
        return experiences.filter(isUserExperience)
    }

    if ('results' in experiences && Array.isArray(experiences.results)) {
        return experiences.results.filter(isUserExperience)
    }

    return Object.values(experiences).filter(isUserExperience)
}

export const findUpgradedOnboardingExperience = (experiences?: UserExperienceCollection | null) => {
    return getExperienceValues(experiences).find((experience) => experience.experience_id === UPGRADED_ONBOARDING)
}

export const getPendingUpgradedOnboardingExperience = (): UserExperience => ({
    experience_id: UPGRADED_ONBOARDING,
    status: STATUS_PENDING,
})

export const shouldFetchExperiences = ({ deviceType, user, hasHash, pathname }: FetchEligibilityParams) => {
    if (!shouldMountUpgradedOnboarding(deviceType)) {
        return false
    }

    if (hasHash) {
        return !!user?.is_upgraded
    }

    return !!user?.is_upgraded && !isAutoOpenExcludedPath(pathname)
}

export const shouldAutoOpenOnboarding = ({ deviceType, user, experience, pathname }: AutoOpenParams) => {
    if (!shouldMountUpgradedOnboarding(deviceType)) {
        return false
    }

    return !!user?.is_upgraded && !isAutoOpenExcludedPath(pathname) && isPendingExperience(experience)
}

export const shouldOpenHashOnboarding = ({ deviceType, user, hasHash }: HashOpenParams) => {
    return shouldMountUpgradedOnboarding(deviceType) && hasHash && !!user?.is_upgraded
}
